import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const LanguageContext = createContext(null);

const LANGUAGES = {
  en: 'English',
  hi: 'हिंदी (Hindi)',
  te: 'తెలుగు (Telugu)',
  fr: 'Français (French)',
  de: 'Deutsch (German)'
};

export const LanguageProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchTranslations = useCallback(async (lang) => {
    try {
      const response = await api.get(`/translations/${lang}`);
      setTranslations(response.data.translations || {});
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get language from user profile or localStorage
    const savedLang = localStorage.getItem('app_language') || user?.language || 'en';
    setLanguage(savedLang);
    fetchTranslations(savedLang);
  }, [user, fetchTranslations]);

  const changeLanguage = useCallback(async (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('app_language', newLang);
    await fetchTranslations(newLang);
    
    // Update user preference if authenticated
    if (isAuthenticated) {
      try {
        await api.patch('/user/language', { language: newLang });
      } catch (error) {
        console.error('Failed to update language preference:', error);
      }
    }
  }, [isAuthenticated, fetchTranslations]);

  // Translation helper function
  const t = useCallback((key, fallback) => {
    return translations[key] || fallback || key;
  }, [translations]);

  const value = {
    language,
    languages: LANGUAGES,
    translations,
    loading,
    changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageContext;
